'use client';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

interface ICountdownCircleProps {
  daysRemaining: number;
  size?: 'small' | 'large';
}

export default function CountdownCircle({
  daysRemaining,
  size = 'large',
}: ICountdownCircleProps) {
  const [animatedValue, setAnimatedValue] = useState(0);
  const targetValue = Math.max(0, Math.min(100, (daysRemaining / 30) * 100));
  const circleSize = size === 'small' ? 72 : 120;

  useEffect(() => {
    const timer = window.setTimeout(() => setAnimatedValue(targetValue), 300);
    return () => window.clearTimeout(timer);
  }, [targetValue]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: circleSize,
        height: circleSize,
        flexShrink: 0,
      }}
    >
      <CircularProgress
        variant="determinate"
        value={animatedValue}
        enableTrackSlot
        size={circleSize}
        thickness={3}
        sx={{
          transform: 'rotate(-90deg)',
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
            stroke: '#005DFF',
            transition: 'stroke-dashoffset 0.8s ease-in-out',
          },
          '& .MuiCircularProgress-track': { stroke: '#CCD0D6' },
        }}
      />
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          inset: 0,
        }}
      >
        <Typography
          variant={size === 'small' ? 'h6' : 'h4'}
          component="span"
          sx={{ lineHeight: 1.1, color: '#001333' }}
        >
          {daysRemaining}
        </Typography>
        <Typography
          variant={size === 'small' ? 'subtitle2' : 'h6'}
          component="span"
          sx={{ lineHeight: 1.1, color: '#001333' }}
        >
          days
        </Typography>
      </Stack>
    </Box>
  );
}
